import React, { useContext } from "react";

import { Box, Text } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import _ from "lodash";

import { Eyebrow } from "./mantine-extended/eyebrow";
import {
  FIXED_SIZE_RANGE_BAR_HIGH_BOUNDARY,
  FIXED_SIZE_RANGE_BAR_LOW_BOUNDARY,
  RangeBar,
  RangeBarWithFixedSize,
  RANGE_BAR_HEIGHT,
  RANGE_BAR_TRANSITION_DURATION,
} from "./RangeBar";

const RangeBarContext = React.createContext({
  percentWidthRangeStart: 0,
  percentWidthRangeEnd: 0,
  sizeVariant: "unknown" as "small" | "large" | "unknown",
});

const FLAGPOLE_BORDER_WIDTH = "1px";
const RANGE_END_FLAG_HEIGHT = "25px";
const CENTER_FLAG_HEIGHT = "60px";

interface DetailedRangeBarProps {
  rangeMin: number;
  rangeLow: number;
  rangeHigh: number;
  rangeMax: number;
}

export function DetailedRangeBar({
  rangeMin,
  rangeLow,
  rangeHigh,
  rangeMax,
}: DetailedRangeBarProps) {
  const totalRange = rangeMax - rangeMin;

  const percentRangeStart = (100 * (rangeLow - rangeMin)) / totalRange;
  const percentRangeEnd = (100 * (rangeHigh - rangeMin)) / totalRange;

  const rangeMid = (rangeHigh + rangeLow) / 2;

  const rangeWingValue = Math.round((100 * (rangeHigh - rangeMid)) / rangeMid);

  const { ref: rangeWidthRef, width: rangeWidth } = useElementSize();

  let sizeVariant: "small" | "large" | "unknown" = "unknown";
  if (_.isNil(rangeWidth)) {
    sizeVariant = "unknown";
  } else {
    sizeVariant = rangeWidth < 100 ? "small" : "large";
  }

  const atHighBoundary = rangeHigh === rangeMax;
  const atLowBoundary = rangeLow === rangeMin;

  return (
    <RangeBarContext.Provider
      value={{
        percentWidthRangeStart: percentRangeStart,
        percentWidthRangeEnd: percentRangeEnd,
        sizeVariant,
      }}
    >
      <Box pos="relative" mt="75px">
        <RangeBar lowPercent={percentRangeStart} highPercent={percentRangeEnd} />

        {/* a dummy element that matches the range's width so we can perform logic */}
        <Box
          ref={rangeWidthRef}
          sx={{
            position: "absolute",
            left: `${percentRangeStart}%`,
            width: `${percentRangeEnd - percentRangeStart}%`,
          }}
        />

        <Flag position="left">{`$${Math.round(rangeLow).toLocaleString("en-US")}`}</Flag>
        <Flag position="center" exaggerated>{`$${Math.round(rangeMid).toLocaleString(
          "en-US",
        )}`}</Flag>
        <Flag position="right">{`$${Math.round(rangeHigh).toLocaleString("en-US")}`}</Flag>

        {!atHighBoundary ? (
          <BookEnd position="start">{`$${Math.round(rangeMin).toLocaleString("en-US")}`}</BookEnd>
        ) : undefined}
        {!atLowBoundary ? (
          <BookEnd position="end">{`$${Math.round(rangeMax).toLocaleString("en-US")}`}</BookEnd>
        ) : undefined}
      </Box>

      <Box pt="14px">
        <Wings>{`${rangeWingValue.toLocaleString("en-US")}%`}</Wings>
      </Box>
    </RangeBarContext.Provider>
  );
}

export function SinglePointRangeBarWithFlag({ value }: { value: number }) {
  const percent =
    ((value - FIXED_SIZE_RANGE_BAR_LOW_BOUNDARY) /
      (FIXED_SIZE_RANGE_BAR_HIGH_BOUNDARY - FIXED_SIZE_RANGE_BAR_LOW_BOUNDARY)) *
    100;

  return (
    <Box pos="relative" mt="75px">
      <RangeBarWithFixedSize lowValue={value} highValue={value} />
      <Box
        sx={{
          position: "absolute",
          left: `calc(${percent}% + 6px)`,
        }}
      >
        <Flag position="center" exaggerated startAtTop>{`$${value.toLocaleString("en-US")}`}</Flag>
      </Box>
    </Box>
  );
}

interface FlagProps {
  children: string;
  position: "left" | "right" | "center";
  exaggerated?: boolean;
  startAtTop?: boolean;
  label?: string;
}

function Flag(props: FlagProps) {
  const context = useContext(RangeBarContext);
  const percentRangeCenter = (context.percentWidthRangeStart + context.percentWidthRangeEnd) / 2;

  return (
    <Box
      className="flagpole-parent"
      sx={{
        position: "absolute",
        width: 0,
        height: props.position === "center" ? CENTER_FLAG_HEIGHT : RANGE_END_FLAG_HEIGHT,
        bottom: 0,
        transition: `left ${RANGE_BAR_TRANSITION_DURATION} ease-in-out`,

        ...(props.position === "left" && {
          left: `${context.percentWidthRangeStart}%}`,
        }),

        ...(props.position === "right" && {
          left: `${context.percentWidthRangeEnd}%}`,
        }),

        ...(props.position === "center" && {
          left: `${percentRangeCenter}%`,
        }),
      }}
    >
      <Box
        className="flagpole"
        sx={() => ({
          display: "block",
          width: 0,
          height: "100%",
          borderColor: props.exaggerated ? "var(--mantine-color-brand-6)" : "#B4B8B7",
          borderStyle: "dashed",
          borderWidth: 0,
          position: "absolute",
          bottom: props.startAtTop ? `${RANGE_BAR_HEIGHT}px` : 0,

          ...(props.position === "left" && {
            right: `calc(50% + ${FLAGPOLE_BORDER_WIDTH} - 1px)`,
            borderLeftWidth: FLAGPOLE_BORDER_WIDTH,
          }),

          ...(props.position === "right" && {
            left: `calc(50% - ${FLAGPOLE_BORDER_WIDTH} + 1px)`,
            borderRightWidth: FLAGPOLE_BORDER_WIDTH,
          }),

          ...(props.position === "center" && {
            left: `calc(50% - ${FLAGPOLE_BORDER_WIDTH} + 1px)`,
            borderRightWidth: FLAGPOLE_BORDER_WIDTH,
          }),
        })}
      ></Box>
      <Box
        className="flag-parent"
        sx={{
          position: "absolute",
          bottom: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Eyebrow
          className="flag"
          // isaac- this is silly to get the flag to be the right size
          size={props.exaggerated ? ("lg" as "md") : "md"}
          sx={{
            padding: "4px 8px",
            border: "1px solid #B4B8B7",
            borderRadius: "40px",
            fontWeight: 600,
            color: "var(--mantine-color-gray-8)",
            backgroundColor: "var(--mantine-color-gray-0)",
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.04), 0px 1px 2px rgba(0, 0, 0, 0.08)",
            transition: `transform ${RANGE_BAR_TRANSITION_DURATION} ease-in-out`,

            ...(props.exaggerated && {
              color: "var(--mantine-color-brand-8)",
              backgroundColor: "var(--mantine-color-brand-0)",
              borderColor: "var(--mantine-color-brand-5)",
            }),

            ...(props.position === "left" &&
              context.sizeVariant === "small" && {
                transform: "translateX(-40%)",
              }),

            ...(props.position === "right" &&
              context.sizeVariant === "small" && {
                transform: "translateX(40%)",
              }),
          }}
        >
          {props.children}
        </Eyebrow>
        {props.label && (
          <Text
            fz="xs"
            sx={{
              color: "var(--mantine-color-brand-8)",
              fontWeight: 500,
              position: "absolute",
              top: "-17px",
            }}
          >
            {props.label}
          </Text>
        )}
      </Box>
    </Box>
  );
}

function Wings({ children }: { children: string }) {
  const middleSpacing = "5px";
  const context = useContext(RangeBarContext);
  const percentRangeCenter = (context.percentWidthRangeStart + context.percentWidthRangeEnd) / 2;

  const widthCss = `calc(${
    percentRangeCenter - context.percentWidthRangeStart
  }% - ${middleSpacing})`;

  return (
    <Box h="14px" w="100%" display="block" sx={{ position: "relative" }}>
      {context.sizeVariant === "large" ? (
        <>
          <Box
            className="wing-left-container"
            sx={{
              position: "absolute",
              left: `${context.percentWidthRangeStart}%`,
              transition: `left ${RANGE_BAR_TRANSITION_DURATION} ease-in-out`,
              height: "8px",
              width: widthCss,
            }}
          >
            <Wing>{children}</Wing>
          </Box>
          <Box
            className="wing-right-container"
            sx={{
              position: "absolute",
              left: `calc(${percentRangeCenter}% + ${middleSpacing})`,
              transition: `left ${RANGE_BAR_TRANSITION_DURATION} ease-in-out`,
              height: "8px",
              width: widthCss,
            }}
          >
            <Wing>{children}</Wing>
          </Box>
        </>
      ) : undefined}
    </Box>
  );
}

function Wing({ children }: { children: string }) {
  const wingBoneWidth = "1px";

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        border: `${wingBoneWidth} solid var(--mantine-color-gray-4)`,
        borderTop: "none",
        borderBottom: "none",
        position: "relative",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box
        h={wingBoneWidth}
        w="100%"
        bg="gray.4"
        sx={{
          position: "absolute",
          top: "50%",
        }}
      />
      <Eyebrow
        size="sm"
        sx={{
          border: "1px solid #B4B8B7",
          color: "var(--mantine-color-gray-6)",
          backgroundColor: "var(--mantine-color-gray-1)",
          borderRadius: "100px",
          textAlign: "center",
          zIndex: 10,
          margin: "0 auto",
          fontSize: "8px",
          padding: "3px 6px",
        }}
      >
        {children}
      </Eyebrow>
    </Box>
  );
}

function BookEnd({ children, position }: { children: string; position: "start" | "end" }) {
  const offset = "-50px";
  const label = position === "start" ? "LOWEST" : "HIGHEST";
  return (
    <Eyebrow
      size="sm"
      sx={{
        padding: "4px 8px",
        border: "1px solid #B4B8B7",
        color: "var(--mantine-color-gray-6)",
        backgroundColor: "var(--mantine-color-gray-1)",
        borderRadius: "100px",
        position: "absolute",
        top: "-6px",

        ...(position === "start" && {
          left: offset,
        }),

        ...(position === "end" && {
          right: offset,
        }),
      }}
    >
      {children}
      <Box
        className="edge-label-low"
        sx={{
          position: "absolute",
          top: "25px",
          fontSize: "10px",
          fontWeight: 600,
          color: "var(--mantine-color-gray-5)",

          ...(position === "start" && {
            left: 7,
          }),

          ...(position === "end" && {
            right: 7,
          }),
        }}
      >
        <span>{label}</span>
      </Box>
    </Eyebrow>
  );
}
